import { motion } from 'framer-motion';
import {
    ArrowRight,
    Building2,
    CheckCircle,
    Code2,
    Key,
    Shield,
    Users,
    Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';

const MDLVerification = () => {
    return (
        <div className="min-h-screen pt-20">
            {/* Hero Section */}
            <section className="py-20 bg-surface-100">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                            mDL Verification
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Seamlessly integrate Mobile Driver&apos;s License (mDL) verification into your existing workflows. Support ISO 18013-5 compliant digital driver&apos;s licenses.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="glass rounded-2xl p-8 border border-white/20">
                                <h2 className="text-lg font-bold text-white mb-6">mDL Verification Process</h2>
                                <div className="space-y-4">
                                    <div className="flex items-center space-x-4 p-4 bg-blue-500/20 rounded-lg border border-blue-500/30">
                                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-white text-sm font-bold">1</span>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white">QR Code Scan</h3>
                                            <p className="text-gray-300 text-sm">User presents mDL via QR code or NFC</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center space-x-4 p-4 bg-green-500/20 rounded-lg border border-green-500/30">
                                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-white text-sm font-bold">2</span>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white">Cryptographic Verification</h3>
                                            <p className="text-gray-300 text-sm">Validate digital signatures and certificate chain</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center space-x-4 p-4 bg-purple-500/20 rounded-lg border border-purple-500/30">
                                        <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-white text-sm font-bold">3</span>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white">Selective Disclosure</h3>
                                            <p className="text-gray-300 text-sm">Access only required information fields</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <h2 className="text-2xl font-bold text-white mb-6">
                                Enterprise mDL Integration
                            </h2>
                            <div className="space-y-6">
                                <div className="flex items-start space-x-4">
                                    <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <CheckCircle className="w-5 h-5 text-green-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">ISO 18013-5 Compliant</h3>
                                        <p className="text-gray-300 text-sm">Full compliance with international mDL standards for maximum interoperability across jurisdictions.</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start space-x-4">
                                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Zap className="w-5 h-5 text-blue-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Real-time Verification</h3>
                                        <p className="text-gray-300 text-sm">Instant verification with tamper detection and validity checking in under 2 seconds.</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start space-x-4">
                                    <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Shield className="w-5 h-5 text-purple-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Privacy Protection</h3>
                                        <p className="text-gray-300 text-sm">Selective disclosure ensures only necessary information is shared, protecting user privacy.</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start space-x-4">
                                    <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Code2 className="w-5 h-5 text-orange-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Easy Integration</h3>
                                        <p className="text-gray-300 text-sm">RESTful APIs and SDKs for seamless integration into existing systems and workflows.</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="text-center"
                    >
                        <div className="glass rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto">
                            <h2 className="text-xl font-bold text-white mb-6">Supported Use Cases</h2>
                            <div className="grid md:grid-cols-4 gap-6">
                                <div className="text-center">
                                    <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                                        <Building2 className="w-6 h-6 text-blue-400" />
                                    </div>
                                    <h3 className="font-semibold text-white mb-1">Age Verification</h3>
                                    <p className="text-gray-400 text-sm">Bars, clubs, retail</p>
                                </div>
                                <div className="text-center">
                                    <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                                        <Shield className="w-6 h-6 text-green-400" />
                                    </div>
                                    <h3 className="font-semibold text-white mb-1">Security Checkpoints</h3>
                                    <p className="text-gray-400 text-sm">Airports, government</p>
                                </div>
                                <div className="text-center">
                                    <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                                        <Users className="w-6 h-6 text-purple-400" />
                                    </div>
                                    <h3 className="font-semibold text-white mb-1">HR Onboarding</h3>
                                    <p className="text-gray-400 text-sm">Employment verification</p>
                                </div>
                                <div className="text-center">
                                    <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                                        <Key className="w-6 h-6 text-orange-400" />
                                    </div>
                                    <h3 className="font-semibold text-white mb-1">Financial Services</h3>
                                    <p className="text-gray-400 text-sm">KYC compliance</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-gradient-to-r from-primary-600 to-neon-600">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                            Ready to Integrate mDL Verification?
                        </h2>
                        <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            Start verifying mobile driver&apos;s licenses with enterprise-grade security and compliance.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href="https://app.passhub.documint.id/signup" target="_blank" rel="noopener noreferrer" className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Start Free Trial
                                <ArrowRight size={20} className="ml-2" />
                            </a>
                            <Link to="/contact" className="border border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Contact Sales
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default MDLVerification;